import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog, ModuleHeader } from 'components/modals';
import type { ActionButton, ViewModeOption } from 'components/modals';
import {
  type ActionMenuItem,
  SyutGrid,
  type SyutGridColumn,
  SyutSelect,
  MinimizedModalStrip,
} from 'components/ui';
import notificationService from 'services/notificationService';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { useAddons, useDeleteAddon } from 'hooks/api/useAddon';
import { propertyService } from 'api/services/propertyService';
import type { Property } from 'api/types';
import type { Addon, AddonSearchRequest } from 'api/types/addon.types';
import type { SelectOption } from 'components/ui/SyutSelect';
import AddonModal from 'components/modals/modules/addon-module/AddonModal';

type ViewMode = 'list';

// Backend's AddonQueryParameters.IncludeInactive is a single bool — there's no
// "show only inactive" channel. We expose only the two states it can honour;
// users wanting to find a specific inactive record can switch to "All Status"
// and use the Code/Name fields.
const statusOptions = [
  { value: '', label: 'All Status' },
  { value: 'true', label: 'Active only' },
];

interface AddonMasterScreenProps {
  moduleIcon?: string;
  tabId?: string;
  moduleName?: string;
  /**
   * Subset of {'add','edit','delete','view','export'} the current user may
   * perform on this module — sourced from admin.tbl_role_permissions via
   * MODULES_CONFIG.actions. Defaults to [] (fail-closed).
   */
  allowedActions?: string[];
}

const AddonMasterScreen: React.FC<AddonMasterScreenProps> = ({
  moduleIcon,
  tabId,
  moduleName,
  allowedActions = [],
}) => {
  const { isMinimized, dismiss: dismissMinimized, getMinimizedModal } = useModalMinimize();

  // --- Property lookup for display names in the grid ---
  const [propertyLookup, setPropertyLookup] = useState<Record<string, Property>>({});

  // --- Server-side pagination + search ---
  const [queryParams, setQueryParams] = useState<AddonSearchRequest>({ page: 1, pageSize: 25 });
  const { data, pagination, refetch } = useAddons(queryParams);
  const { deleteAddon, loading: isDeleting } = useDeleteAddon();

  // --- UI state ---
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);

  // --- Filter state ---
  const [filterCode, setFilterCode] = useState('');
  const [filterName, setFilterName] = useState('');
  const [filterProperty, setFilterProperty] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  // --- Modal state ---
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit' | 'copy' | 'view'>('create');
  const [selectedRecord, setSelectedRecord] = useState<Addon | null>(null);
  const createInstanceCounter = useRef(0);
  const [createInstanceId, setCreateInstanceId] = useState<string>('0');
  const copyInstanceCounter = useRef(0);
  const [copyInstanceId, setCopyInstanceId] = useState<string>('0');
  const formModalMinimizeRef = useRef<(() => void) | null>(null);

  // --- FK lookup loading (3 mandatory sites: mount, save success, restore handlers) ---
  const loadLookups = useCallback(async () => {
    try {
      const propRes = await propertyService.getAll({ pageSize: 500 });
      const lookup: Record<string, Property> = {};
      propRes.data.forEach((p) => {
        lookup[p.id] = p;
      });
      setPropertyLookup(lookup);
    } catch {
      /* ignore */
    }
  }, []);
  useEffect(() => {
    loadLookups();
  }, [loadLookups]);

  // --- Async property options for filter dropdown ---
  const loadPropertyOptions = useCallback(async (inputValue: string): Promise<SelectOption[]> => {
    const res = await propertyService.getAll({
      searchTerm: inputValue || undefined,
      pageSize: 50,
      includeInactive: false,
    });
    return (res.data || []).map((p) => ({
      value: p.id,
      label: `${p.propertyCode} - ${p.propertyName}`,
    }));
  }, []);

  // --- Search debounce ---
  useEffect(() => {
    const timer = setTimeout(() => {
      setQueryParams((prev) => ({ ...prev, searchTerm: searchQuery || undefined, page: 1 }));
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // --- Pagination handlers ---
  const handlePageChange = useCallback((pageIndex: number) => {
    setQueryParams((prev) => ({ ...prev, page: pageIndex + 1 }));
  }, []);
  const handlePageSizeChange = useCallback((pageSize: number) => {
    setQueryParams((prev) => ({ ...prev, pageSize, page: 1 }));
  }, []);

  // --- View handler ---
  const handleView = useCallback(
    (a: Addon) => {
      const viewId = tabId ? `${tabId}-view-modal-${a.id}` : null;
      if (viewId && isMinimized(viewId)) {
        const modal = getMinimizedModal(viewId);
        dismissMinimized(viewId);
        modal?.onRestore?.();
        return;
      }
      setSelectedRecord(a);
      setFormMode('view');
      setFormModalOpen(true);
    },
    [tabId, isMinimized, dismissMinimized, getMinimizedModal]
  );

  // --- Edit handler ---
  const handleEdit = useCallback(
    (a: Addon) => {
      const modalId = tabId ? `${tabId}-form-modal-edit-${a.id}` : null;
      if (modalId && isMinimized(modalId)) {
        const modal = getMinimizedModal(modalId);
        dismissMinimized(modalId);
        modal?.onRestore?.();
        return;
      }
      setSelectedRecord(a);
      setFormMode('edit');
      setFormModalOpen(true);
    },
    [tabId, isMinimized, dismissMinimized, getMinimizedModal]
  );

  // --- Copy handler (multi-instance) ---
  const handleCopy = useCallback(
    (a: Addon) => {
      if (formModalOpen) {
        formModalMinimizeRef.current?.();
      }
      const newId = String(++copyInstanceCounter.current);
      setCopyInstanceId(newId);
      setSelectedRecord(a);
      setFormMode('copy');
      setFormModalOpen(true);
    },
    [formModalOpen]
  );

  // --- Add New handler ---
  const handleAddNew = useCallback(() => {
    const newId = String(++createInstanceCounter.current);
    setCreateInstanceId(newId);
    setFormMode('create');
    setSelectedRecord(null);
    setFormModalOpen(true);
  }, []);

  // --- Delete handler ---
  const handleDeleteClick = useCallback((a: Addon) => {
    setSelectedRecord(a);
    setDeleteModalOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!selectedRecord) return;
    try {
      await deleteAddon(selectedRecord.id as string, selectedRecord.rowVersion);
      setDeleteModalOpen(false);
      notificationService.toast.success(`"${selectedRecord.addonName}" has been deleted successfully.`);
      setSelectedRecord(null);
      refetch();
    } catch {
      notificationService.toast.error('An error occurred while deleting. Please try again.');
    }
  }, [selectedRecord, deleteAddon, refetch]);

  const handleSaveSuccess = useCallback(() => {
    setFormModalOpen(false);
    setSelectedRecord(null);
    refetch();
    loadLookups();
  }, [refetch, loadLookups]);

  const handleRestoreModal = useCallback(
    (restoredMode: 'create' | 'edit' | 'copy' | 'view', instanceId?: string, recordCode?: string) => {
      loadLookups();
      if (instanceId) {
        if (restoredMode === 'copy') setCopyInstanceId(instanceId);
        else if (restoredMode === 'create') setCreateInstanceId(instanceId);
      }
      if (recordCode) {
        const rec = data.find((r) => r.addonCode === recordCode);
        if (rec) setSelectedRecord(rec);
      }
      setFormMode(restoredMode);
      setFormModalOpen(true);
    },
    [data, loadLookups]
  );

  // --- Grid columns ---
  const tableColumns: SyutGridColumn<Addon>[] = useMemo(
    () => [
      { id: 'addonCode', header: 'Code', accessorKey: 'addonCode', size: 140, enableSorting: true },
      { id: 'addonName', header: 'Name', accessorKey: 'addonName', size: 240, enableSorting: true },
      {
        id: 'property',
        header: 'Property',
        accessorKey: 'propertyId',
        size: 220,
        enableSorting: false,
        cell: ({ getValue }) => {
          const pid = getValue() as string | undefined;
          if (!pid) return <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>—</span>;
          const prop = propertyLookup[pid];
          return (
            <span style={{ fontSize: '0.85rem' }}>
              {prop ? `${prop.propertyCode} - ${prop.propertyName}` : pid}
            </span>
          );
        },
      },
      { id: 'description', header: 'Description', accessorKey: 'description', size: 320, enableSorting: false },
      {
        id: 'isActive',
        header: 'Status',
        accessorKey: 'isActive',
        size: 110,
        cell: ({ getValue }) => {
          const isActive = getValue() as boolean;
          return (
            <span className={`badge badge-${isActive ? 'active' : 'inactive'}`}>
              {isActive ? 'Active' : 'Inactive'}
            </span>
          );
        },
      },
    ],
    [propertyLookup]
  );

  // --- Row actions gated by allowedActions ---
  const getRowActions = useCallback(
    (a: Addon): ActionMenuItem[] => {
      const items: ActionMenuItem[] = [];
      if (allowedActions.includes('view')) {
        items.push({ id: 'view', label: 'View Details', icon: <IconSystem.eye size={18} />, onClick: () => handleView(a) });
      }
      if (allowedActions.includes('edit')) {
        items.push({ id: 'edit', label: 'Edit', icon: <IconSystem.edit size={18} />, onClick: () => handleEdit(a) });
      }
      if (allowedActions.includes('add')) {
        items.push({ id: 'copy', label: 'Copy', icon: <IconSystem.copy size={18} />, onClick: () => handleCopy(a) });
      }
      if (allowedActions.includes('delete')) {
        items.push({
          id: 'delete',
          label: 'Delete',
          icon: <IconSystem.trash size={18} />,
          onClick: () => handleDeleteClick(a),
          variant: 'danger',
        });
      }
      return items;
    },
    [allowedActions, handleView, handleEdit, handleCopy, handleDeleteClick]
  );

  // --- View mode options ---
  const viewModeOptions: ViewModeOption[] = [
    { id: 'list', icon: <IconSystem.list size={16} />, label: 'List View', tooltip: 'List View' },
  ];

  // --- Action buttons ---
  const actionButtons: ActionButton[] = useMemo(() => {
    const buttons: ActionButton[] = [];
    if (allowedActions.includes('add')) {
      buttons.push({
        id: 'add',
        icon: <IconSystem.add size={18} />,
        label: 'Add Add-on',
        onClick: handleAddNew,
        tooltip: 'Add New Add-on',
        variant: 'icon',
      });
    }
    buttons.push({
      id: 'refresh',
      icon: <IconSystem.refresh size={18} />,
      label: 'Refresh',
      onClick: () => refetch(),
      tooltip: 'Refresh',
      variant: 'icon',
    });
    return buttons;
  }, [allowedActions, refetch, handleAddNew]);

  // --- Filter Apply / Clear ---
  // Filter-panel Code and Name share a single backend channel (`searchTerm`),
  // which the AddonRepository ILIKE-matches against code/name/description. When
  // both are filled, Code wins (more specific). The header search bar (top of
  // the screen) is a separate channel; clicking Apply takes ownership of
  // `searchTerm`, so the panel filter is what shows after Apply.
  const handleFilterApply = useCallback(() => {
    const panelSearchTerm = filterCode.trim() || filterName.trim() || undefined;
    setQueryParams((prev) => ({
      ...prev,
      propertyId: filterProperty || undefined,
      // Backend exposes only IncludeInactive (true|false). 'Active only' →
      // IncludeInactive=false. 'All Status' (default) → IncludeInactive=true.
      // 'Inactive only' isn't supported by the current backend param.
      includeInactive: filterStatus === 'true' ? false : true,
      searchTerm: panelSearchTerm,
      page: 1,
    }));
    // Sync the header search bar so the user sees a single consistent search
    // string and the debounce effect (which writes searchTerm from searchQuery)
    // doesn't tug the filter back when it next fires.
    setSearchQuery(panelSearchTerm ?? '');
    // Panel stays open after Apply so the user can iterate on filters without
    // re-opening it each time.
  }, [filterProperty, filterStatus, filterCode, filterName]);

  const handleFilterClear = useCallback(() => {
    setFilterCode('');
    setFilterName('');
    setFilterProperty('');
    setFilterStatus('');
    setSearchQuery('');
    setQueryParams((prev) => ({
      ...prev,
      propertyId: undefined,
      includeInactive: undefined,
      searchTerm: undefined,
      page: 1,
    }));
  }, []);

  return (
    <div className="module-master-container">
      <ModuleHeader
        title="Add-on Master"
        description="Manage ancillary add-on items and services that can be attached to bookings"
        icon={
          moduleIcon && (IconSystem as any)[moduleIcon]
            ? React.createElement((IconSystem as any)[moduleIcon], { size: 18 })
            : undefined
        }
        searchConfig={{
          value: searchQuery,
          onChange: setSearchQuery,
          placeholder: 'Search add-ons...',
        }}
        viewModeConfig={{
          currentMode: viewMode,
          modes: viewModeOptions,
          onChange: (mode) => setViewMode(mode as ViewMode),
        }}
        filterConfig={{
          onClick: () => setFilterOpen(!filterOpen),
          isActive: filterOpen,
          appliedCount: [filterCode, filterName, filterProperty, filterStatus].filter(Boolean).length,
        }}
        leadingToolbarItems={tabId ? <MinimizedModalStrip tabId={tabId} /> : undefined}
        actions={actionButtons}
      />

      <div className="module-content" style={{ marginTop: '6px' }}>
        <div className="data-grid-container">
          <SyutGrid
            data={data}
            columns={tableColumns}
            enableRowSelection
            enablePagination
            manualPagination
            totalCount={pagination.totalItems}
            currentPage={(queryParams.page ?? 1) - 1}
            pageSize={queryParams.pageSize ?? 25}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            rowActions={getRowActions}
            emptyMessage="No add-ons found"
          />
        </div>

        {filterOpen && (
          <div className="search-panel">
            <div className="search-panel-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                <IconSystem.filter size={14} />
                <h3 className="search-panel-title">Filters</h3>
              </div>
              <button
                onClick={() => setFilterOpen(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0.25rem',
                  display: 'flex',
                  alignItems: 'center',
                  color: 'var(--text-muted)',
                  borderRadius: '0.25rem',
                }}
                title="Close filters"
              >
                <IconSystem.x size={16} />
              </button>
            </div>

            <div className="search-panel-content">
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>
                  Code
                </label>
                <input
                  type="text"
                  className="form-input"
                  value={filterCode}
                  onChange={(e) => setFilterCode(e.target.value)}
                  placeholder="Filter by code..."
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }}
                />
              </div>

              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>
                  Name
                </label>
                <input
                  type="text"
                  className="form-input"
                  value={filterName}
                  onChange={(e) => setFilterName(e.target.value)}
                  placeholder="Filter by name..."
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }}
                />
              </div>

              <div className="filter-section">
                <SyutSelect
                  label="Property"
                  isAsync
                  loadOptions={loadPropertyOptions}
                  defaultOptions
                  cacheOptions={false}
                  value={filterProperty}
                  onChange={(val: string | string[] | null) => setFilterProperty((val as string) || '')}
                  placeholder="Select property..."
                  isClearable
                  size="sm"
                />
              </div>

              <div className="filter-section">
                <SyutSelect
                  label="Status"
                  options={statusOptions}
                  value={filterStatus}
                  onChange={(val: string | string[] | null) => setFilterStatus((val as string) || '')}
                  placeholder="Select status..."
                  isClearable={false}
                  size="sm"
                />
              </div>
            </div>

            <div className="filter-actions">
              <button className="btn btn-secondary" onClick={handleFilterClear}>
                Clear
              </button>
              <button className="btn btn-primary" onClick={handleFilterApply}>
                Apply
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Form Modal — handles create / edit / copy / view via mode prop */}
      <AddonModal
        isOpen={formModalOpen}
        mode={formMode}
        record={selectedRecord}
        onSave={handleSaveSuccess}
        onClose={() => setFormModalOpen(false)}
        tabId={tabId}
        moduleName={moduleName}
        onRestoreModal={handleRestoreModal}
        createInstanceId={createInstanceId}
        copyInstanceId={copyInstanceId}
        minimizeRef={formModalMinimizeRef}
        propertyLookup={propertyLookup}
      />

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={deleteModalOpen}
        title="Confirm Delete"
        message={`Are you sure you want to delete "${selectedRecord?.addonName}"? This action cannot be undone.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        loading={isDeleting}
        loadingMessage="Deleting..."
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteModalOpen(false)}
      />
    </div>
  );
};

export default AddonMasterScreen;
